import { useMemo } from "react";
import { CalendarClock, Receipt, WalletCards, BarChart3 } from "lucide-react";
import { Page, Panel, Table, StatusPill } from "../lib/ui";
import { money, humanDate } from "../lib/api";

export default function Payments({ records, invoices, cheques, canSeeCheques }) {
  const items = useMemo(() => {
    const list = [];
    for (const r of records) {
      if (r.status === "Bekliyor") {
        list.push({
          id: `r-${r.id}`,
          date: r.date,
          title: r.title,
          type: r.type === "income" ? "Tahsilat" : "Ödeme",
          direction: r.type === "income" ? "in" : "out",
          amount: r.amount,
          source: "Gelir/Gider",
          status: r.status,
        });
      }
    }
    for (const i of invoices) {
      if (!["Ödendi", "İptal"].includes(i.status)) {
        list.push({
          id: `i-${i.id}`,
          date: i.due_date || i.date,
          title: `${i.invoice_no} · ${i.customer_name}`,
          type: "Fatura tahsilatı",
          direction: "in",
          amount: i.total,
          source: "Fatura",
          status: i.status,
        });
      }
    }
    if (canSeeCheques) {
      for (const c of cheques) {
        if (c.status === "Portföyde") {
          list.push({
            id: `c-${c.id}`,
            date: c.maturity_date,
            title: `${c.check_no || "Çek"} · ${c.customer_name || "-"}`,
            type: c.direction === "received" ? "Alınan çek" : "Verilen çek",
            direction: c.direction === "received" ? "in" : "out",
            amount: c.amount,
            source: "Çek",
            status: c.status,
          });
        }
      }
    }
    return list.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [records, invoices, cheques, canSeeCheques]);

  const totals = useMemo(() => {
    const income = items.filter((x) => x.direction === "in").reduce((s, x) => s + x.amount, 0);
    const expense = items.filter((x) => x.direction === "out").reduce((s, x) => s + x.amount, 0);
    return { income, expense };
  }, [items]);

  return (
    <>
      <Page
        eyebrow="AKIŞ TAKVİMİ"
        title="Ödemeler & Tahsilatlar"
        subtitle="Yaklaşan ödemeler, tahsilatlar ve vadesi gelen çekler."
      />

      <div className="mini-stats">
        <div><span>Yaklaşan tahsilat</span><strong className="positive">+{money(totals.income)}</strong></div>
        <div><span>Yaklaşan ödeme</span><strong className="negative">-{money(totals.expense)}</strong></div>
        <div><span>Kayıt sayısı</span><strong>{items.length}</strong></div>
      </div>

      <Panel title="Yaklaşan işlemler" subtitle="Tarihe göre sıralanmış" className="module-panel">
        <Table
          testid="payments-table"
          columns={[
            { label: "Tarih" }, { label: "İşlem" }, { label: "Tür" },
            { label: "Kaynak" }, { label: "Durum" }, { label: "Tutar", align: "right" },
          ]}
          rows={items}
          empty="Yaklaşan işlem yok. Faturalar, kayıtlar ve çekler burada listelenir."
          renderRow={(r) => (
            <tr key={r.id} data-testid={`payment-row-${r.id}`}>
              <td>{humanDate(r.date)}</td>
              <td><strong>{r.title}</strong></td>
              <td>
                <span className="type-tag">
                  {r.source === "Fatura" ? <Receipt size={11} /> : r.source === "Çek" ? <WalletCards size={11} /> : <BarChart3 size={11} />}
                  {r.type}
                </span>
              </td>
              <td>{r.source}</td>
              <td><StatusPill status={r.status} /></td>
              <td className={`amount ${r.direction === "in" ? "positive" : "negative"}`}>
                {r.direction === "in" ? "+" : "-"}{money(r.amount)}
              </td>
            </tr>
          )}
        />
      </Panel>
    </>
  );
}
